import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  ArrowLeft,
  Loader2,
  CreditCard,
  Save,
  CheckCircle,
  Gift,
  LogOut,
} from "lucide-react";

const ReferralPartnerSettings = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partner, setPartner] = useState(null);
  
  // Payment method state
  const [payoutMethod, setPayoutMethod] = useState("");
  const [accountInfo, setAccountInfo] = useState("");

  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchPartnerData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/referrals/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("Access denied. You are not a referral partner.");
          navigate("/login");
          return;
        }
        throw new Error("Failed to fetch data");
      }

      const data = await response.json();
      setPartner(data.partner);
      
      // Set saved payment method if exists
      if (data.payout_method?.type) {
        setPayoutMethod(data.payout_method.type);
        setAccountInfo(data.payout_method.account_info || "");
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [API, token, navigate]);

  useEffect(() => {
    fetchPartnerData();
  }, [fetchPartnerData]);

  const handleSavePaymentMethod = async () => {
    if (!payoutMethod) {
      toast.error("Please select a payment method");
      return;
    }
    
    if (!accountInfo.trim()) {
      toast.error("Please enter your account information");
      return;
    }

    setSaving(true);

    try {
      const params = new URLSearchParams();
      params.append("method_type", payoutMethod);
      params.append("account_info", accountInfo.trim());

      const response = await fetch(`${API}/api/referrals/partner/payout-method?${params.toString()}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to save payment method");
      }

      toast.success("Payment method saved successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to save payment method");
    } finally {
      setSaving(false);
    }
  };

  const getPlaceholder = () => {
    switch (payoutMethod) {
      case "paypal":
        return "your.email@example.com";
      case "cashapp":
        return "$yourcashtag";
      case "venmo":
        return "@yourusername";
      default:
        return "Select a payment method first";
    }
  };

  const getLabel = () => {
    switch (payoutMethod) {
      case "paypal":
        return "PayPal Email";
      case "cashapp":
        return "Cash App $Cashtag";
      case "venmo":
        return "Venmo Username";
      default:
        return "Account Information";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rnb-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-rnb-blue mx-auto mb-4" />
          <p className="text-rnb-gray">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rnb-light">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rnb-teal/10 flex items-center justify-center">
                <Gift className="h-5 w-5 text-rnb-teal" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-rnb-navy">
                  Partner Settings
                </h1>
                <p className="text-sm text-rnb-gray">{partner?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-rnb-gray hover:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/partner")}
          className="flex items-center text-rnb-blue hover:text-rnb-navy transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </button>

        {/* Payment Method Card */}
        <Card className="card-modern">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-rnb-navy flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Method
            </CardTitle>
            <CardDescription>
              Save your preferred payment method for receiving commission payouts. 
              This information will be pre-filled when you request a payout.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Payout Method</Label>
              <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                <SelectTrigger data-testid="settings-payout-method">
                  <SelectValue placeholder="Select your preferred payout method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">PayPal</span>
                      <span className="text-rnb-gray text-sm">- Pay via email</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="cashapp">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Cash App</span>
                      <span className="text-rnb-gray text-sm">- Pay via $Cashtag</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="venmo">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Venmo</span>
                      <span className="text-rnb-gray text-sm">- Pay via username</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-info">{getLabel()}</Label>
              <Input
                id="account-info"
                value={accountInfo}
                onChange={(e) => setAccountInfo(e.target.value)}
                placeholder={getPlaceholder()}
                disabled={!payoutMethod}
                data-testid="settings-account-info"
              />
              {payoutMethod && (
                <p className="text-xs text-rnb-gray">
                  {payoutMethod === "paypal" && "Enter the email address linked to your PayPal account"}
                  {payoutMethod === "cashapp" && "Enter your Cash App $Cashtag (include the $ symbol)"}
                  {payoutMethod === "venmo" && "Enter your Venmo username (include the @ symbol)"}
                </p>
              )}
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Payouts are processed within 7-10 business days. 
                Minimum payout amount is $50. Make sure your payment information is accurate 
                to avoid delays.
              </p>
            </div>

            <Button
              onClick={handleSavePaymentMethod}
              disabled={saving || !payoutMethod || !accountInfo.trim()}
              className="w-full btn-primary"
              data-testid="save-payment-method"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Payment Method
                </>
              )}
            </Button>

            {partner?.payout_method?.type && (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>
                  Current method: {partner.payout_method.type.charAt(0).toUpperCase() + partner.payout_method.type.slice(1)} 
                  ({partner.payout_method.account_info})
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Info Card */}
        <Card className="card-modern mt-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-rnb-navy">
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-rnb-light rounded-lg">
                <p className="text-sm text-rnb-gray mb-1">Name</p>
                <p className="font-medium text-rnb-navy">{partner?.name}</p>
              </div>
              <div className="p-4 bg-rnb-light rounded-lg">
                <p className="text-sm text-rnb-gray mb-1">Email</p>
                <p className="font-medium text-rnb-navy">{partner?.email}</p>
              </div>
              <div className="p-4 bg-rnb-light rounded-lg">
                <p className="text-sm text-rnb-gray mb-1">Referral Code</p>
                <p className="font-mono font-bold text-rnb-teal">{partner?.referral_code}</p>
              </div>
              <div className="p-4 bg-rnb-light rounded-lg">
                <p className="text-sm text-rnb-gray mb-1">Member Since</p>
                <p className="font-medium text-rnb-navy">
                  {partner?.joined_at ? new Date(partner.joined_at).toLocaleDateString() : "N/A"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ReferralPartnerSettings;
